import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

import insertSpaceIntoEmptyTags from '../helpers/insertSpaceIntoEmptyTags.helper';

type TReadyContentSource = {
  editor: Editor;
  key: string;
  content: string;
};

// A part-loaded document holds one part at a time, so the editor renders a slice of its source.
const isRenderedFromSource = (rendered: string, source: string): boolean =>
  rendered.length === 0 ? source.length === 0 : source.includes(rendered);

/** Tracks whether the current editor has rendered its current document content source. */
const useEditorContentReady = (editor: Editor | null, contentSourceKey: string, content: string | undefined) => {
  const [readySource, setReadySource] = useState<TReadyContentSource>();

  useEffect(() => {
    if (!editor || content === undefined) return;
    const renderedContent = insertSpaceIntoEmptyTags(content);

    const handleEditorContentSet = (event: CustomEvent<{ content: string; editor: Editor }>) => {
      if (event.detail.editor === editor && isRenderedFromSource(event.detail.content, renderedContent)) {
        setReadySource({ editor, key: contentSourceKey, content });
      }
    };

    window.addEventListener('editorContentSet', handleEditorContentSet);
    return () => window.removeEventListener('editorContentSet', handleEditorContentSet);
  }, [editor, content, contentSourceKey]);

  const acknowledgeSavedContent = useCallback(
    (savedEditor: Editor, savedContent: string) => {
      setReadySource((source) =>
        source?.editor === savedEditor && source.key === contentSourceKey
          ? { editor: savedEditor, key: contentSourceKey, content: savedContent }
          : source
      );
    },
    [contentSourceKey]
  );

  return {
    isContentReady: readySource?.key === contentSourceKey && readySource.content === content,
    acknowledgeSavedContent,
  };
};

export default useEditorContentReady;
